// Shared date formatting — everything displays in Eastern Time (America/New_York).
//
// Two kinds of values flow in from the backend:
//  - Instants (activity log created_at, lastPriceUpdate, invoice dates): stored/sent
//    in UTC. SQLite's "YYYY-MM-DD HH:MM:SS" and zoneless ISO strings would be
//    parsed as browser-local by new Date(), so parseUtcDate normalizes them first.
//  - Event dates ("2026-01-09T19:00:00" or "2026-08-15"): venue-local wall clock.
//    These must display exactly as written — converting would shift showtimes,
//    and date-only strings would show the previous day.
const TZ = 'America/New_York';
const SQLITE_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/;
const ZONELESS_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

// Parse a backend/Skybox timestamp into the correct UTC instant
export function parseUtcDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  let s = String(value).trim();
  if (SQLITE_RE.test(s)) s = s.replace(' ', 'T') + 'Z';
  else if (ZONELESS_ISO_RE.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDateTimeEastern(value, opts = {}) {
  const d = parseUtcDate(value);
  if (!d) return '-';
  return d.toLocaleString('en-US', {
    timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', ...opts
  });
}

export function formatDateEastern(value, opts = {}) {
  const d = parseUtcDate(value);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', {
    timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric', ...opts
  });
}

export function formatTimeEastern(value, opts = {}) {
  const d = parseUtcDate(value);
  if (!d) return '-';
  return d.toLocaleTimeString('en-US', {
    timeZone: TZ, hour: 'numeric', minute: '2-digit', second: '2-digit', ...opts
  });
}

// Load wall-clock components as UTC so formatting with timeZone:'UTC'
// reproduces them verbatim regardless of the browser's zone
function parseWallClock(value) {
  const m = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return {
    date: new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0))),
    hasTime: m[4] !== undefined
  };
}

export function formatEventDateTime(value, opts = {}) {
  const p = parseWallClock(value);
  if (!p) return value ? String(value) : '';
  return p.date.toLocaleString('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    ...(p.hasTime ? { hour: 'numeric', minute: '2-digit' } : {}), ...opts
  });
}

export function formatEventDateOnly(value, opts = {}) {
  const p = parseWallClock(value);
  if (!p) return value ? String(value) : '-';
  return p.date.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric', ...opts
  });
}

// An event's time as the venue wrote it ("5:30 PM"); '' for a date with no
// time, no value or junk
export function formatEventTimeOnly(value) {
  const p = parseWallClock(value);
  if (!p || !p.hasTime) return '';
  return p.date.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' });
}

// Relative age for a UTC instant: "just now", "5m ago", "2h ago", "3d ago"
export function timeAgo(value) {
  if (!value) return '';
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// Eastern calendar date "YYYY-MM-DD" for an instant (default now); mirrors
// backend/utils/dates.js. Built from parts so a locale's date pattern can't
// change the shape.
const dayFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit'
});

export function easternDateString(d = new Date()) {
  const p = Object.fromEntries(dayFmt.formatToParts(d).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

// "YYYY-MM-DD" + n days (pure calendar math; mirrors backend/utils/dates.js)
export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
